import type { Card } from '@campfire/cards';
import { h } from './dom.ts';

const SUIT_GLYPH: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_NAME: Record<string, string> = { S: 'spades', H: 'hearts', D: 'diamonds', C: 'clubs' };

export function cardLabel(c: Card): string {
  return c.rank === 'JK' ? 'Joker' : `${c.rank}${SUIT_GLYPH[c.suit ?? ''] ?? ''}`;
}

export function rankPlural(rank: string): string {
  const names: Record<string, string> = { A: 'Aces', K: 'Kings', Q: 'Queens', J: 'Jacks' };
  return names[rank] ?? `${rank}s`;
}

export interface CardOpts {
  readonly selected?: boolean | undefined;
  /** Chosen for a multi-card action: greyed in place in the hand while it shows in the tray. */
  readonly picked?: boolean | undefined;
  readonly onclick?: (() => void) | undefined;
  readonly small?: boolean | undefined;
  readonly testid?: string | undefined;
}

export function cardFace(c: Card, opts: CardOpts = {}): HTMLElement {
  const red = c.suit === 'H' || c.suit === 'D';
  const glyph = SUIT_GLYPH[c.suit ?? ''] ?? '★';
  const cls = ['card', red ? 'red' : 'black', opts.selected ? 'selected' : '', opts.picked ? 'picked' : '', opts.small ? 'small' : ''].filter(Boolean).join('.');
  return h(
    `${opts.onclick ? 'button' : 'div'}.${cls}`,
    {
      'data-card': c.id,
      'data-rank': c.rank,
      'data-testid': opts.testid,
      'aria-label': c.rank === 'JK' ? 'Joker' : `${c.rank} of ${SUIT_NAME[c.suit ?? '']}`,
      onclick: opts.onclick ? () => opts.onclick?.() : undefined,
    },
    h('span.corner', {}, c.rank === 'JK' ? 'JK' : c.rank, h('span.suit', {}, glyph)),
    h('span.pip', {}, glyph),
  );
}

/** Cards picked for the next action, lifted out of the hand so overlaps never hide them (tap to put back). */
export function pickTray(cards: readonly Card[], unpick: (id: string) => void, clear: () => void): HTMLElement {
  return h(
    'div.tray',
    { 'data-testid': 'tray' },
    cards.map((c) => cardFace(c, { onclick: () => unpick(c.id), testid: `tray-${c.id}` })),
    // Done arranging? Put every picked card back at once (FR-UX-10).
    h('button.link.tray-return', { 'data-testid': 'tray-return', onclick: clear }, 'Return to hand'),
  );
}

export function cardBack(opts: { onclick?: (() => void) | undefined; testid?: string | undefined; small?: boolean; label?: string } = {}): HTMLElement {
  return h(
    `${opts.onclick ? 'button' : 'div'}.card.back${opts.small ? '.small' : ''}`,
    { onclick: opts.onclick ? () => opts.onclick?.() : undefined, 'data-testid': opts.testid, 'aria-label': opts.label ?? 'face-down card' },
  );
}

/** A stack of face-down cards with a count badge. */
export function pile(count: number, testid?: string): HTMLElement {
  return h(
    'div.pile',
    { 'data-testid': testid, 'data-count': count },
    count > 0 ? cardBack() : h('div.card.empty'),
    h('span.count', {}, String(count)),
  );
}
